import { PageLayout } from "components/layout";
import { AudioPlayerView, ChapterItem, TabItems } from "../components";
import { useContext, useEffect, useState } from "react";
import { Text, useAudioPlayer } from "components/common";
import { css } from "@emotion/css";
import { PodcastHeader } from "../components/header";
import { recordActivity } from "services";
import { EActivityEvent, PodcastResponse } from "types";
import { useLazyQuery } from "@apollo/client";
import { PODCAST } from "apollo";
import { UserContext } from "context";
import { useDemoMode, usePageViewed } from "hooks";
import { fakePodcast } from "demo-data";
import { format } from "date-fns";

export const PodcastScreen = () => {
  const [tab, setTab] = useState<"CHAPTERS" | "TRANSCRIPT">("CHAPTERS");
  const [chapter, setChapter] = useState<
    {
      id: string;
      name: string;
      src: string;
      transcript: string;
      duration: number;
    }[]
  >([]);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const { state: userState } = useContext(UserContext);
  const { demoMode } = useDemoMode();

  usePageViewed(EActivityEvent.PodcastPageViewed);

  const [loadPodast, { loading }] = useLazyQuery<PodcastResponse>(PODCAST);

  const {
    addTrack,
    play,
    pause,
    currentTime,
    seekToTrackIndex,
    seekToTime,
    currentTrackIndex,
    isPlaying,
  } = useAudioPlayer();

  useEffect(() => {
    return () => {
      pause();
    };
  }, [pause]);

  useEffect(() => {
    const demoPodcast = demoMode
      ? fakePodcast.map((item) => ({
          id: item.chapter!,
          name: item.chapter!,
          src: item.url!,
          transcript: item.transcript!,
          duration: item.audioDuration!,
        }))
      : [];
    setChapter(demoPodcast);

    for (const item of demoPodcast) {
      if (item.src) {
        addTrack(item.src);
      }
    }
    const dayOffset = 0;
    loadPodast({
      fetchPolicy: "network-only",
      //pollInterval: demoMode ? undefined : 60000, // 1 minute
      variables: {
        dayOffset,
      },
    }).then((result) => {
      if (result.data?.podcasts && !demoMode) {
        const date = new Date(result.data.podcasts[0].created);
        setLastUpdated(format(date, "MMMM do • h:mm a"));
        setChapter(
          result.data.podcasts
            .map((item: any) => ({
              id: item?._id,
              name: item?.title,
              src: item?.url,
              transcript: item?.transcript,
              duration: item?.audioDuration,
            }))
            .filter((item: any) => item?.src),
        );
        for (const item of result.data.podcasts) {
          if (item?.url) {
            addTrack(item.url);
          }
        }
      }
    });
  }, [addTrack, loadPodast, demoMode]);

  const handlePlay = async () => {
    play();
    try {
      if (userState?._id) {
        await recordActivity({
          data: {
            user: userState?._id,
            event: EActivityEvent.PodcastPlayed,
            source: "web",
            data: {
              chapter: chapter[currentTrackIndex].name,
              id: chapter[currentTrackIndex].id,
              app: "web",
            },
          },
        });
      }
    } catch (error) {}
  };

  if (loading) {
    return <Text.Body>Loading...</Text.Body>;
  }

  return (
    <PageLayout title="Podcast | Multiplayer">
      {/* <CallInterface title="Daily briefing" participants={participants} /> */}
      <div
        className={css`
          width: 100%;
          max-width: 688px;
          margin: auto;
          padding: 0px;
        `}
      >
        {/* <MultiplayerLogoSimple style={{ marginBottom: "24px" }} /> */}
        <div>
          <PodcastHeader lastUpdated={lastUpdated} />
        </div>
        <div
          className={css`
            width: fit-content;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            margin-top: 24px;
            margin-bottom: 32px;
          `}
        >
          <TabItems
            title="CHAPTERS"
            onClick={() => {
              setTab("CHAPTERS");
            }}
            active={tab === "CHAPTERS"}
          />
          <TabItems
            title="TRANSCRIPT"
            onClick={() => {
              setTab("TRANSCRIPT");
            }}
            active={tab === "TRANSCRIPT"}
          />
        </div>
        <div
          className={css`
            width: 100%;
            max-height: 253px;
            margin-bottom: 24px;
            overflow-x: auto;
            scrollbar-color: #29282f #1a191e;
            scrollbar-width: thin;
            &::-webkit-scrollbar {
              width: 8px;
            }
            &::-webkit-scrollbar-track {
              background: #1a191e;
            }
            @media (max-height: 700px) {
              max-height: 223px;
            }
          `}
        >
          {chapter?.length === 0 && (
            <Text.Body>
              No podcast available yet. You will receive your podcast everyday
              at 6:00 AM from Monday to Friday.
            </Text.Body>
          )}
          {tab === "CHAPTERS" ? (
            chapter.map((item, index) => (
              <ChapterItem
                key={item.name}
                title={item.name}
                playing={currentTrackIndex === index && isPlaying}
                onClick={() => {
                  const trackIndex = currentTrackIndex;
                  seekToTrackIndex(index);
                  setTimeout(() => {
                    if (isPlaying && trackIndex === index) {
                      pause();
                    } else {
                      handlePlay();
                    }
                  }, 300);
                }}
                duration={item.duration}
              />
            ))
          ) : (
            <Text.Body>
              {chapter[currentTrackIndex]?.transcript ||
                "You can read the transcript of the chapter being played here"}
            </Text.Body>
          )}
        </div>
        <AudioPlayerView
          isPlaying={isPlaying}
          play={handlePlay}
          pause={pause}
          seekToTime={seekToTime}
          currentTime={currentTime}
          duration={chapter[currentTrackIndex]?.duration}
        />
      </div>
    </PageLayout>
  );
};
